import { Button } from '@mui/material';
import {
  type ComponentType,
  type FormEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { type UUID } from '~/types/common';

import FeatureService from '~/services/feature.service';

import { FilterGroupFilter } from '~/models/filters/FilterGroupFilter';
import FilterNew from '~/models/filters/FilterNew';

import { clone, isObjectContentEqual } from '@vestigas/utils';
import { type FilterContext } from '~/utils/filters';

import { Icon, Reveal } from '@vestigas/ui/atoms';
import BasicForm from '~/components/BasicForm';
import { emptyDeliveryNoteFilterGroup } from '~/components/deliveries/deliveryOverview/DeliveryFilterGroups/utils';
import { ClientPortalTooltip } from '~/components/salesPackages/clientPortal/ClientPortalTooltip';
import { PackageBasicRestrictionTooltip } from '~/components/salesPackages/packageBasicRestriction/PackageBasicRestrictionTooltip';

import { FilterGroupsList } from './FilterGroupsList';
import { FilterRow } from './FilterRow';
import {
  type FilterGroupMark,
  type FilterGroupObject,
  type Filters,
  type SelectableFilter,
} from './types';

type P = {
  baseTestId: string;
  currentFilterGroupObject: FilterGroupObject;
  deleteFilterGroup: () => void;
  emptyFilterGroupObject: Filters;
  disableDeleteButton: boolean;
  disableUpdateButton: boolean;
  filterContext: FilterContext;
  filterGroupMarks: FilterGroupMark[];
  filterGroupOpen: boolean;
  filterGroups: FilterGroupObject[];
  filterRows: string[];
  hideFilterRows: boolean;
  hideEditNameIcon: boolean;
  hideUpdateDeleteButton: boolean;
  onChangeValue: (
    filterRow: string,
    customField: any,
    filterValue: any[],
  ) => void;
  onClickExpandFilterGroup: () => void;
  onClickFilterGroup: (filterGroupId: UUID) => void;
  onOpenFilterRow: (filterId: UUID) => void;
  originalFilterGroupObject: Record<string, any>;
  renderFilterRow: ComponentType<any>;
  resetSelectedFilterGroup: () => void;
  saveNewFilterGroup: (
    filterGroupId: UUID,
    filterGroupName: string | undefined,
    filterRows: string[],
    hasChanges: boolean,
  ) => void;
  selectableFilters: SelectableFilter[];
  selectedFilterGroup: string;
  updateFilterGroup: (filterRows: string[]) => void;
  updateFilterGroupName: (
    filterGroupId: string,
    filterGroupName: string | undefined,
  ) => void;
  updateFilterRows: (filterRows: string[]) => void;
  withExpandableFilterGroup: boolean;
};

export function FilterGroups({
  baseTestId,
  currentFilterGroupObject,
  deleteFilterGroup,
  emptyFilterGroupObject = emptyDeliveryNoteFilterGroup,
  disableDeleteButton,
  disableUpdateButton,
  filterContext,
  filterGroupMarks,
  filterGroupOpen,
  filterGroups,
  filterRows,
  hideFilterRows,
  hideEditNameIcon,
  hideUpdateDeleteButton,
  onChangeValue,
  onClickExpandFilterGroup,
  onClickFilterGroup,
  onOpenFilterRow,
  originalFilterGroupObject,
  renderFilterRow,
  resetSelectedFilterGroup,
  saveNewFilterGroup,
  selectableFilters,
  selectedFilterGroup,
  updateFilterGroup,
  updateFilterGroupName: updateFilterGroupNameProperty,
  updateFilterRows,
  withExpandableFilterGroup,
}: P) {
  const [formType, setFormType] = useState('create');
  const [formOpen, setFormOpen] = useState(false);
  const [deleteFormOpen, setDeleteFormOpen] = useState(false);
  const [newFilterGroupName, setNewFilterGroupName] = useState<
    string | undefined
  >('');

  const hasUnusedFilters = selectableFilters.some(
    ({ id }) => !filterRows.includes(id),
  );
  const appliedFilterValues = filterRows.map((filterRow) => {
    const selectedFilter = selectableFilters.find(({ id }) => id === filterRow);
    const selectedValues =
      (selectedFilter?.customField
        ? currentFilterGroupObject?.filters?.selectedCustomFields?.find(
            ({ key }) => key === selectedFilter.customField.key,
          )?.filterValue
        : currentFilterGroupObject.filters[filterRow]) ?? [];

    return selectedValues.filter(Boolean);
  });
  const everyAppliedFilterHasAValue = appliedFilterValues.every(
    (filterValues) => Boolean(filterValues?.length > 0),
  );
  const hasAnyFilterValues = appliedFilterValues.some(
    (filterValues) => filterValues.length > 0,
  );
  const canAddFilter = everyAppliedFilterHasAValue && hasUnusedFilters;

  const resetSelectedFilterGroupHandler = () => {
    if (!selectedFilterGroup) {
      return false;
    }

    if (filterGroups.some(({ id }) => id === selectedFilterGroup)) {
      return false;
    }

    resetSelectedFilterGroup();
    return true;
  };

  const getFilterRowsFromFilterGroup = (filterGroup: FilterGroupObject) => {
    const filterRows = [...filterGroup.filterRows];

    // Extend the filter rows by the props that are filtered on.
    for (const [key, value] of Object.entries(filterGroup.filters)) {
      // If no filter is applied or the filter is already in the list of filter rows, there is no use in extending the filter row list.
      if (!value?.length || filterRows.includes(key)) {
        continue;
      }

      // If the filter is not the custom fields filter, we can handle it normally.
      if (key !== FilterGroupFilter.FILTER.SELECTED_CUSTOM_FIELDS) {
        filterRows.push(key);
        continue;
      }

      // If the filter is the custom fields filter, we have to handle it differently because custom fields filters are stored with a different format.
      for (const customField of value) {
        // Prevent duplicate filter rows.
        if (filterRows.includes(customField.key)) {
          continue;
        }

        filterRows.push(customField.key);
      }
    }

    return filterRows;
  };

  const initFilterRows = useCallback(() => {
    // Get filter rows from current filter group
    let newFilterRows = getFilterRowsFromFilterGroup(currentFilterGroupObject);

    // If no filter rows found, try to get them from localStorage
    if (newFilterRows.length === 0 && originalFilterGroupObject?.filterRows) {
      newFilterRows = [...originalFilterGroupObject.filterRows];
    }

    // Filter out disabled filters
    newFilterRows = newFilterRows.filter((filterRow) => {
      const selectableFilter = selectableFilters.find(
        ({ id }) => id === filterRow,
      );
      return selectableFilter && !selectableFilter.disabled;
    });

    // Add default filter if empty
    if (newFilterRows.length === 0) {
      const selectableFilter = selectableFilters.find(
        ({ disabled }) => !disabled,
      );
      if (selectableFilter) {
        newFilterRows.push(selectableFilter.id);
      }
    }

    updateFilterRows(newFilterRows);
    if (hasAnyFilterValues && !filterGroupOpen) {
      onClickExpandFilterGroup();
    }
  });

  const removeDisabledFilterRows = () => {
    const newFilterRows = [...filterRows].filter((filterRow) => {
      const selectableFilter = selectableFilters.find(
        ({ id }) => id === filterRow,
      );

      if (!selectableFilter) {
        return true;
      }

      return !selectableFilter.disabled;
    });

    updateFilterRows(newFilterRows);
  };

  useEffect(() => {
    const selectedFilterGroupReset = resetSelectedFilterGroupHandler();
    if (!selectedFilterGroupReset) {
      initFilterRows();
    }
  }, []);

  useEffect(() => {
    if (currentFilterGroupObject?.id) {
      initFilterRows();
    }
  }, [currentFilterGroupObject?.id]);

  useEffect(() => {
    if (filterRows.length > 0) {
      removeDisabledFilterRows();
    }
  }, [selectableFilters]);

  const showFilterRows = useMemo(() => {
    if (hideFilterRows) {
      return false;
    }

    if (filterGroupOpen) {
      return true;
    }

    return Boolean(filterGroups?.length === 0);
  }, [filterGroupOpen, filterGroups, hideFilterRows]);

  const filterGroupHasBeenUpdated = () => {
    const mergedOriginalFilterGroupObjectFilters = {
      ...emptyFilterGroupObject,
      ...originalFilterGroupObject?.filters,
    };

    const currentFilters = currentFilterGroupObject?.filters;

    const currentCustomFields = currentFilters?.selectedCustomFields || [];
    const originalCustomFields =
      mergedOriginalFilterGroupObjectFilters?.selectedCustomFields || [];

    // Custom field filters have a different structure and need to be compared separately
    const isCustomFieldFiltersEqual = isObjectContentEqual(
      currentCustomFields,
      originalCustomFields,
    );

    // Remove custom fields before comparing other filters
    const currentFiltersWithoutCustom = { ...currentFilters };
    const originalFiltersWithoutCustom = {
      ...mergedOriginalFilterGroupObjectFilters,
    };
    delete currentFiltersWithoutCustom.selectedCustomFields;
    delete originalFiltersWithoutCustom.selectedCustomFields;

    // Compare regular filters
    const isRegularFiltersEqual = isObjectContentEqual(
      currentFiltersWithoutCustom,
      originalFiltersWithoutCustom,
    );

    return !isCustomFieldFiltersEqual || !isRegularFiltersEqual;
  };

  const handleClickFilterGroup = (filterGroupId: string) => {
    onClickFilterGroup(filterGroupId);
  };

  const handleClickNewFilterGroup = (event: MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();

    setFormOpen(true);
    setFormType('create');
  };

  const handleClickChangeFilterGroupName = (
    event: MouseEvent<SVGSVGElement>,
  ) => {
    event.stopPropagation();
    event.currentTarget.blur();

    setFormOpen(true);
    setFormType('edit');
    setNewFilterGroupName(
      filterGroups.find(({ id }) => id === selectedFilterGroup)?.name,
    );
  };

  const handleClickExpandFilterGroup = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    onClickExpandFilterGroup();
  };

  const handleSaveNewFilterGroup = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (formType === 'create') {
      saveNewFilterGroup(
        uuidv4(),
        newFilterGroupName,
        filterRows,
        filterGroupHasBeenUpdated(),
      );
    } else {
      updateFilterGroupNameProperty(selectedFilterGroup, newFilterGroupName);
    }

    setFormOpen(false);
    setNewFilterGroupName('');
  };

  const handleAbortNewFilterGroup = () => {
    setFormOpen(false);
    setNewFilterGroupName('');
  };

  const handleUpdateFilterGroup = () => {
    updateFilterGroup(filterRows);
  };

  const handleChangeProperty = (
    oldFilterProperty: string,
    newFilterProperty: string,
  ) => {
    const oldSelectedFilter = selectableFilters.find(
      ({ id }) => id === oldFilterProperty,
    );

    // Reset old filter row value.
    onChangeValue(oldFilterProperty, oldSelectedFilter?.customField, []);

    // Replace old filter row with new filter row.
    const filterRowIndex = filterRows.indexOf(oldFilterProperty);
    const newFilterRows = [...filterRows];
    newFilterRows[filterRowIndex] = newFilterProperty;

    updateFilterRows(newFilterRows);
  };

  const handleDeleteRow = (deletedFilterRow: UUID) => {
    const oldSelectedFilter = selectableFilters.find(
      (selectableFilter) => selectableFilter.id === deletedFilterRow,
    );

    onChangeValue(deletedFilterRow, oldSelectedFilter?.customField, []);

    // If there is only one filter left, just reset it, but don't remove it so that the list of filters isn't empty.
    if (filterRows.length === 1) {
      return;
    }

    const filterRowIndex = filterRows.indexOf(deletedFilterRow);
    const newFilterRows = [...filterRows];
    newFilterRows.splice(filterRowIndex, 1);

    updateFilterRows(newFilterRows);
  };

  const handleOpenFilterRow = (filterId: UUID) => {
    if (onOpenFilterRow) {
      onOpenFilterRow(filterId);
    }
  };

  const handleAddFilterRow = () => {
    const availableFilters = selectableFilters.filter(
      ({ disabled, id }) => !filterRows.includes(id) && !disabled,
    );

    if (availableFilters.length === 0) {
      return;
    }

    updateFilterRows([...filterRows, availableFilters[0]?.id]);
  };

  const handleDeleteFormSuccess = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    deleteFilterGroup();
    setDeleteFormOpen(false);
  };

  const getUpdateButton = () => (
    <Button
      color="primary"
      data-testid={`${baseTestId}_update_group_button`}
      disabled={!filterGroupHasBeenUpdated() || disableUpdateButton}
      startIcon={
        !filterGroupHasBeenUpdated() || disableUpdateButton ? (
          <Icon metaphor="check" size="inherit" />
        ) : null
      }
      variant={
        !filterGroupHasBeenUpdated() || disableUpdateButton
          ? 'text'
          : 'contained'
      }
      onClick={handleUpdateFilterGroup}
    >
      {!filterGroupHasBeenUpdated() || disableUpdateButton
        ? 'Filtergruppe gespeichert'
        : 'Filtergruppe speichern'}
    </Button>
  );

  const getDeleteButton = () => (
    <>
      <Button
        color="secondary"
        data-testid={`${baseTestId}_delete_group_button`}
        disabled={disableDeleteButton}
        startIcon={<Icon metaphor="deleteOutlined" size="inherit" />}
        variant="outlined"
        onClick={() => {
          setDeleteFormOpen(true);
        }}
      >
        Filtergruppe löschen
      </Button>
      <BasicForm
        formAbort={() => {
          setDeleteFormOpen(false);
        }}
        formSuccess={handleDeleteFormSuccess}
        open={deleteFormOpen}
        submitButtonTitle="Löschen"
        testId={`${baseTestId}_delete_group_form`}
        title="Filtergruppe löschen"
      >
        Willst du mit dem Löschen fortfahren?
      </BasicForm>
    </>
  );

  const getButtons = () => {
    if (
      !selectedFilterGroup ||
      filterGroups.length === 0 ||
      hideUpdateDeleteButton
    ) {
      return null;
    }

    return (
      <div className="mt-4 flex items-center justify-between gap-4">
        {getUpdateButton()}
        {getDeleteButton()}
      </div>
    );
  };

  return (
    <>
      <FilterGroupsList
        abortNewFilterGroup={handleAbortNewFilterGroup}
        baseTestId={baseTestId}
        filterGroupHasChanges={filterGroupHasBeenUpdated()}
        filterGroupMarks={filterGroupMarks}
        filterGroups={filterGroups}
        hideEditNameIcon={hideEditNameIcon}
        hideFilterRows={hideFilterRows}
        isCollapsed={!filterGroupOpen}
        isCreating={formType === 'create'}
        isSubmitting={false}
        saveNewFilterGroup={handleSaveNewFilterGroup}
        selectedFilterGroup={selectedFilterGroup}
        state={{ formOpen, formType, newFilterGroupName }}
        withExpandableFilterGroup={withExpandableFilterGroup}
        onChangeName={setNewFilterGroupName}
        onClickChangeFilterGroupName={handleClickChangeFilterGroupName}
        onClickExpandFilterGroup={handleClickExpandFilterGroup}
        onClickFilterGroup={handleClickFilterGroup}
        onClickNewFilterGroup={handleClickNewFilterGroup}
      />
      <Reveal
        show={showFilterRows}
        testId={`${baseTestId}_filter_rows_accordion`}
      >
        <div className="mt-2 flex flex-col gap-2">
          {filterRows.map((filterRow) => {
            const newSelectedFilter = clone(
              selectableFilters.find(({ id }) => id === filterRow),
            );

            if (!newSelectedFilter) {
              return null;
            }

            const newSelectedValues =
              (newSelectedFilter.customField
                ? currentFilterGroupObject.filters.selectedCustomFields?.find(
                    ({ key }) => key === newSelectedFilter.customField.key,
                  )?.filterValue
                : clone(currentFilterGroupObject.filters[filterRow])) ?? [];

            const FilterRowComponent = renderFilterRow || FilterRow;

            return (
              <FilterRowComponent
                key={newSelectedFilter.id}
                filterContext={filterContext}
                hideDeleteButton={filterRows.length === 1}
                isDisabled={newSelectedFilter.disabled}
                operator={FilterNew.OPERATOR.IS_ANY_OF}
                selectableFilters={selectableFilters.filter(
                  ({ id }) => id === filterRow || !filterRows.includes(id),
                )}
                selectedFilter={newSelectedFilter}
                selectedValues={newSelectedValues}
                testId={`${baseTestId}_filter_row`}
                onChangeOperator={() => {}} // Feature FILTER_OPERATOR
                onChangeProperty={(value) => {
                  handleChangeProperty(filterRow, value);
                }}
                onChangeValue={(_, filterValue) => {
                  onChangeValue(
                    filterRow,
                    newSelectedFilter.customField,
                    filterValue,
                  );
                }}
                onDeleteRow={() => {
                  handleDeleteRow(filterRow);
                }}
                onOpen={() => {
                  handleOpenFilterRow(newSelectedFilter.id);
                }}
              />
            );
          })}
        </div>
        {canAddFilter ? (
          <PackageBasicRestrictionTooltip>
            <ClientPortalTooltip>
              <Button
                className="!mt-2 w-80"
                color="primary"
                data-testid={`${baseTestId}_add_filter_button`}
                disabled={
                  FeatureService.clientPortal() ||
                  FeatureService.packageBasicRestriction()
                }
                startIcon={<Icon metaphor="add" size="inherit" />}
                variant="outlined"
                onMouseDown={handleAddFilterRow}
              >
                Weiteren Filter hinzufügen
              </Button>
            </ClientPortalTooltip>
          </PackageBasicRestrictionTooltip>
        ) : null}
        {getButtons()}
      </Reveal>
    </>
  );
}
